import React from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  CircularProgress,
  makeStyles,
  styled,
  Avatar,
  Tooltip,
  Backdrop
} from '@material-ui/core'
import { AvatarGroup } from '@material-ui/lab'

import {
  getQueuesCounterRSPA,
  getPickupQueue,
  clearPickupQueues,
  editPickupQueueRSPA,
  deletePickupQueueRSPA
} from '../store/pickup_queue/actions'
import { showAlertMessage } from '../store/alert/actions'

import { extractDayRange } from '../utils/extractDate'
import ConfirmModal from '../components/modal/ConfirmModal'
import useQueueWebSocket from '../utils/useQueueWebSocket'
import CustomTable from '../components/table/CustomTable'
import UserAvatar from '../components/shared/UserAvatar'
import StyledSVG from '../components/shared/StyledSVG'
import { numberFromText } from '../utils/helpers'
import { colors } from '../utils/constants'

import createQueueSuccessSVG from '../assets/icons/success-pickup-backdrop.svg'
import pickupIcon from '../assets/icons/pickup-icon.svg'
import configGearIcon from '../assets/icons/config-gear-icon.svg'
import roundedAddIcon from '../assets/icons/rounded-add-cross-icon.svg'
import roundedGreenCheck from '../assets/icons/rounded-green-check.svg'
import trashIcon from '../assets/icons/trash.svg'

const useStyle = makeStyles(theme => ({
  root: {
    paddingTop: 36,
    fontFamily: 'Montserrat, Source Sans Pro',
    width: '96%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 72
  },
  title_section: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 16,
    '& .title': {
      margin: 0,
      color: '#386093',
      fontWeight: 600,
      fontSize: 32
    },
    '& .subtitle': {
      margin: 0,
      fontWeight: 400,
      fontSize: 16
    }
  },
  info_banners: {
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    gap: 32
  },
  banners_section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    '& h1.title': {
      color: '#386093',
      fontWeight: 600,
      fontSize: 28,
      margin: 0
    }
  },
  avatar_wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    '& p': {
      fontWeight: 500,
      fontSize: 16,
      color: '#555555'
    }
  },
  without_photo_preview: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '17px',
    letterSpacing: '0em',
    textAlign: 'left'
  },
  finish_pickup_queue: {
    display: 'flex',
    alignItems: 'center',
    columnGap: 32,
    '& > div': {
      cursor: 'pointer'
    },
    '& .red-trash > path': {
      stroke: '#EF7C8F'
    }
  },
  backdrop_icon_wrapper: {
    width: 400,
    height: 320,
    borderRadius: 24,
    backgroundColor: '#386093',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '32px 56px 48px 48px',
    rowGap: 16,
    '& .title': {
      margin: 0,
      color: '#F1F3F4',
      fontWeight: 600,
      fontSize: 24
    },
    '& .subtitle': {
      margin: 0,
      color: '#FFFFFF',
      fontWeight: 400,
      fontSize: 14
    }
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1
  },
  queues_section: {
    '& .title': {
      margin: 0,
      color: '#386093',
      fontSize: 28,
      fontWeight: 600,
      marginBottom: 32
    }
  },
  default_row_style: {
    backgroundColor: '#FFFFFF !important',
    '& td:last-child': {
      borderTopRightRadius: '16px',
      borderBottomRightRadius: '16px'
    },
    '& td:first-child': {
      borderTopLeftRadius: '16px',
      borderBottomLeftRadius: '16px'
    }
  },
  temporary_row_style: {
    backgroundColor: '#F8E7D6 !important',
    '& td:last-child': {
      borderTopRightRadius: '16px',
      borderBottomRightRadius: '16px'
    },
    '& td:first-child': {
      borderTopLeftRadius: '16px',
      borderBottomLeftRadius: '16px'
    }
  },
  queue_table: {
    '& .MuiTable-root': {
      backgroundColor: 'unset',
      borderCollapse: 'separate !important',
      borderSpacing: '0px 12px !important'
    }
  }
}))

const PickupCard = styled('div')(({ theme, bgColor, textColor }) => ({
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  padding: 32,
  borderRadius: 16,
  backgroundColor: bgColor,
  color: textColor,
  gridColumn: 'span 4',
  height: 240,
  gap: 20,
  '& svg': {
    '& path': {
      fill: textColor
    }
  },
  '& .banner_title': {
    fontSize: 28,
    margin: 0
  },
  '& .banner_data': {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-end',
    '& p': {
      margin: 0
    },
    '& .value': {
      fontSize: 48,
      fontWeight: 600,
      lineHeight: '42px'
    },
    '& .label': {
      fontSize: 16,
      fontWeight: 500
    }
  }
}))

const PickupOption = styled('div')(({ bgColor }) => ({
  padding: '32px 48px',
  display: 'flex',
  gridColumn: 'span 6',
  gap: 16,
  backgroundColor: bgColor,
  borderRadius: 16,
  height: 130,
  alignItems: 'center',
  cursor: 'pointer',
  '& .labels': {
    display: 'flex',
    flexDirection: 'column',
    color: '#FFFFFF',
    gap: 8,
    '& h1': {
      fontWeight: 600,
      fontSize: 28,
      margin: 0
    },
    '& p': {
      fontWeight: 500,
      fontSize: 16,
      margin: 0
    }
  }
}))

const CounterValue = ({ value, isFetching }) => {
  if (isFetching) {
    return <CircularProgress />
  }
  return <p className='value'>{value}</p>
}

const PickupPage = ({
  getQueuesCounterRSPA,
  queues,
  clearPickupQueues,
  editPickupQueueRSPA,
  deletePickupQueueRSPA,
  getPickupQueue,
  schoolId,
  currentUser,
  alert
}) => {
  const classes = useStyle()
  const history = useHistory()
  const today = new Date()
  const { beginningOfDay, endOfDay } = extractDayRange(today)
  const [backdrop, setBackdrop] = React.useState({ open: false, svg: null })
  const [confirmModal, setConfirmModal] = React.useState({
    open: false,
    parent: null,
    child: null,
    id: null
  })
  const { error } = useQueueWebSocket({
    fractalId: currentUser.attributes.fractal_id,
    schoolId
  })
  const defaultParams = {
    'q[updated_at_gteq]': beginningOfDay,
    'q[updated_at_lteq]': endOfDay,
    'q[status_eq]': 0,
    include:
      'approved_by,pickup_authorization,pickup_queue_children.child,school,user'
  }
  const [queusCounter, setQueuesCounter] = React.useState({
    isFetching: true,
    data: {}
  })
  const authorizationType = {
    permanent: 'Permanente',
    temporary: 'Temporário'
  }

  const columns = [
    {
      key: 'parent',
      name: 'Responsável',
      width: '25%',
      render: row => {
        const parentName = row?.user?.name || row?.pickup_authorization?.name
        const parentPhoto =
          row?.user?.avatar_url || row?.pickup_authorization?.photo_url
        return (
          <div className={classes.avatar_wrapper}>
            <UserAvatar
              avatarStyle={{
                width: 40,
                height: 40
              }}
              withoutUserName
              userName={parentName}
              avatarUrl={parentPhoto}
            />
            <p>{parentName}</p>
          </div>
        )
      }
    },
    {
      key: 'child',
      name: 'Aluno(s)',
      width: '25%',
      render: row => {
        return (
          <div className={classes.avatar_wrapper}>
            <AvatarGroup max={5} spacing={1}>
              {row?.pickup_queue_children?.map((pckQueueChild, idx) => {
                const index =
                  numberFromText(pckQueueChild?.child?.name?.slice(0, 2)) %
                  colors.length
                return (
                  <Tooltip
                    key={`child[${idx}]`}
                    title={pckQueueChild?.child?.name}
                    arial-label='avatar-usuario'
                  >
                    <Avatar
                      style={{
                        height: '30px',
                        width: '30px',
                        backgroundColor:
                          // eslint-disable-next-line standard/computed-property-even-spacing
                          colors[index]
                      }}
                      src={pckQueueChild?.child?.avatar_url}
                    >
                      <p className={classes.without_photo_preview}>
                        {pckQueueChild?.child?.name[0]}
                      </p>
                    </Avatar>
                  </Tooltip>
                )
              })}
            </AvatarGroup>
            <p>{row?.pickup_queue_children?.[0]?.child?.name}</p>
          </div>
        )
      }
    },
    {
      key: 'type',
      name: 'Tipo',
      width: '15%',
      render: row => {
        const isParent = !row.authorization_id && row.user_id
        return (
          <div>
            <p>
              {isParent
                ? 'Responsável autorizado'
                : authorizationType[row?.pickup_authorization?.pickup_type]}
            </p>
          </div>
        )
      }
    },
    {
      key: 'vehicle_plate',
      name: 'Placa',
      width: '10%',
      render: row => {
        return <p>{row?.pickup_authorization?.vehicle_plate.toUpperCase()}</p>
      }
    },
    {
      key: 'estimated_arrival',
      name: 'Estimativa',
      width: '10%',
      render: row => {
        return <p>{row?.estimated_arrival}</p>
      }
    },
    {
      key: 'finish_pickup_queue',
      name: '',
      width: '5%',
      render: row => {
        return (
          <div className={classes.finish_pickup_queue}>
            <Tooltip
              title={'Concluir retirada'}
              arial-label='concluir-retirada'
            >
              <div onClick={() => handleConfirmFinishQueue(row.id)}>
                <StyledSVG src={roundedGreenCheck} width={28} height={28} />
              </div>
            </Tooltip>
            <Tooltip title={'Excluir retirada'} arial-label='excluir-retirada'>
              <div onClick={() => handleConfirmModal(row)}>
                <StyledSVG
                  className='red-trash'
                  src={trashIcon}
                  width={28}
                  height={28}
                />
              </div>
            </Tooltip>
          </div>
        )
      }
    }
  ]

  React.useEffect(() => {
    getQueuesCounterRSPA().then(res => {
      setQueuesCounter({
        isFetching: false,
        data: res.data
      })
    })
    getPickupQueue({
      page: 1,
      params: {
        ...defaultParams
      }
    })
    return () => {
      clearPickupQueues()
    }
  }, [])

  React.useEffect(
    () => {
      if (error) {
        alert('Erro ao conectar à fila.', 'error')
      }
    },
    [error]
  )

  const handleDeletePickupQueue = id => {
    deletePickupQueueRSPA(confirmModal.id)
      .then(res => {
        getPickupQueue({
          page: 1,
          params: {
            ...defaultParams
          }
        })
      })
      .catch(error => {
        console.error(error)
        alert('Erro ao excluir registro de retirada.', 'error')
      })
  }

  const handleConfirmModal = queue => {
    const reponsibleName =
      queue?.pickup_authorization?.name || queue?.user?.name
    setConfirmModal(prev => ({
      ...prev,
      open: true,
      id: queue.id,
      parent: reponsibleName,
      child: queue?.pickup_queue_children
        ?.map(item => item?.child?.name)
        ?.join(', ')
    }))
  }

  const handleConfirmFinishQueue = async id => {
    const newValues = {
      data: {
        type: 'pickup_queues',
        id,
        attributes: {
          status: 'finished'
        }
      }
    }
    setBackdrop(prev => ({ ...prev, open: true }))
    await editPickupQueueRSPA({ id, data: newValues })
      .then(res => {
        setBackdrop(prev => ({ ...prev, svg: createQueueSuccessSVG }))
        getPickupQueue({
          page: 1,
          params: {
            ...defaultParams
          }
        })
      })
      .catch(err => {
        console.error(err)
        setBackdrop({ svg: null, open: false })
        alert('Erro ao concluir a retirada.', 'error')
      })
  }

  const applyStyleByRowAttribute = row => {
    if (row?.pickup_authorization?.pickup_type === 'temporary') {
      return classes.temporary_row_style
    }
    return classes.default_row_style
  }

  return (
    <div className={classes.root}>
      <div className={classes.title_section}>
        <h1 className='title'>Painel de saídas e autorizações</h1>
        <p className='subtitle'>
          Acompanhe o moveimento atual e gerencia as permissões de retirada.
        </p>
      </div>
      <div className={classes.banners_section}>
        <h1 className='title'>Administração</h1>
        <div className={classes.info_banners}>
          <PickupCard
            onClick={() => history.push('/pickup/history')}
            bgColor='#D9E8FA'
            textColor='#386093'
          >
            <StyledSVG src={pickupIcon} height={40} width={40} />
            <h1 className='banner_title'>Total de saídas</h1>
            <div className='banner_data'>
              <CounterValue
                isFetching={queusCounter.isFetching}
                value={queusCounter.data?.all_queues}
              />
              <p className='label'>Histórico geral</p>
            </div>
          </PickupCard>
          <PickupCard
            onClick={() => history.push('/pickup/today')}
            bgColor='#F4D8CA'
            textColor='#555555'
          >
            <StyledSVG src={pickupIcon} height={40} width={40} />
            <h1 className='banner_title'>Saídas hoje</h1>
            <div className='banner_data'>
              <CounterValue
                isFetching={queusCounter.isFetching}
                value={queusCounter.data?.today_queues}
              />
              <p className='label'>Movimento diário</p>
            </div>
          </PickupCard>
          <PickupCard
            onClick={() => history.push('/pickup/pending')}
            bgColor='#EF7C8F'
            textColor='#FFFFFF'
          >
            <StyledSVG src={pickupIcon} height={40} width={40} />
            <h1 className='banner_title'>Pendências</h1>
            <div className='banner_data'>
              <CounterValue
                isFetching={queusCounter.isFetching}
                value={queusCounter.data?.pending_authorizations}
              />
              <p className='label'>Histórico geral</p>
            </div>
          </PickupCard>
          <PickupOption
            onClick={() => history.push('/pickup/manual')}
            bgColor='#8B598E'
          >
            <StyledSVG src={roundedAddIcon} width={56} height={56} />
            <div className='labels'>
              <h1>Retirada manual</h1>
              <p>Registre manualmente uma retirada</p>
            </div>
          </PickupOption>
          <PickupOption
            onClick={() => history.push('/pickup/authorizations')}
            bgColor='#F4A87C'
          >
            <StyledSVG src={configGearIcon} width={56} height={56} />
            <div className='labels'>
              <h1>Gerenciar permissão</h1>
              <p>Edite e gerencie reponsáveis já cadastrados</p>
            </div>
          </PickupOption>
        </div>
      </div>
      <div className={classes.queues_section}>
        <h1 className='title'>Status Atual</h1>
        <CustomTable
          columns={columns}
          from={'pickup_queue'}
          data={queues.items}
          classRoot={classes.queue_table}
          applyStyleByRowAttribute={applyStyleByRowAttribute}
          params={{
            ...defaultParams
          }}
          fetchItems={getPickupQueue}
          isFetching={queues.isFetching}
          pagination={queues.pagination}
          border={'none'}
          headCellRadius
          headColor={'rgba(173, 184, 204, 0.3)'}
        />
        <Backdrop
          onClick={() => setBackdrop(prev => ({ svg: null, open: false }))}
          className={classes.backdrop}
          open={backdrop.open}
        >
          {backdrop.svg ? (
            <div className={classes.backdrop_icon_wrapper}>
              <StyledSVG width={120} height={110} src={createQueueSuccessSVG} />
              <h1 className='title'>Retirada Concluída</h1>
              <p className='subtitle'>
                Retirada concluída com sucesso. Tenha um ótimo dia!
              </p>
            </div>
          ) : (
            <CircularProgress />
          )}
        </Backdrop>
        <ConfirmModal
          title={`Deseja excluir a retirada de ${
            confirmModal?.child
          } destinada à ${confirmModal?.parent} ?`}
          open={confirmModal.open}
          setOpen={value => {
            if (value) {
              setConfirmModal({ ...confirmModal, open: value })
            } else {
              setConfirmModal({
                id: null,
                open: value,
                child: null,
                parent: null
              })
            }
          }}
          onConfirm={() => {
            handleDeletePickupQueue(confirmModal.id)
          }}
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state, props) => {
  return {
    queues: state.pickup_queues,
    schoolId: state.school?.currentSchool?.school?.id,
    currentUser: state.auth.user.data
  }
}
const mapDispatchToProps = dispatch => ({
  getQueuesCounterRSPA: data => dispatch(getQueuesCounterRSPA.request(data)),
  getPickupQueue: data => dispatch(getPickupQueue(data)),
  clearPickupQueues: () => dispatch(clearPickupQueues()),
  editPickupQueueRSPA: data => dispatch(editPickupQueueRSPA.request(data)),
  deletePickupQueueRSPA: data => dispatch(deletePickupQueueRSPA.request(data)),
  alert: (message, type) =>
    dispatch(
      showAlertMessage({
        message: message,
        severity: type
      })
    )
})

export default connect(mapStateToProps, mapDispatchToProps)(PickupPage)
